import React, { useEffect, useState } from 'react';
import { supabase } from '../config/supabase';
import { supabaseAdmin } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import he from 'he';
import './PushNotifications.css';

export default function PushNotifications() {
  const { isAdministrator } = useAuth();
  const [users, setUsers] = useState([]);
  /** Liczba odbiorców „wszyscy” bez pobierania listy urządzeń (tylko dla kont niebędących administratorem). */
  const [recipientCount, setRecipientCount] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectRecipientsManually, setSelectRecipientsManually] = useState(false); // tylko dla admina
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    type: 'info',
    target: 'all', // 'all' or 'specific'
    userId: '',
  });

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        if (isAdministrator) {
          await cleanupOldNotifications();
          await loadUsers();
        } else {
          await loadRecipientCount();
        }
        await loadNotifications();
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [isAdministrator]);

  const cleanupOldNotifications = async () => {
    try {
      // Oblicz datę sprzed 7 dni
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const sevenDaysAgoISO = sevenDaysAgo.toISOString();

      // Usuń powiadomienia starsze niż 7 dni
      const { data, error } = await supabaseAdmin
        .from('push_notifications')
        .delete()
        .lt('sent_at', sevenDaysAgoISO)
        .select('id');

      if (error) throw error;
      
      const deletedCount = (data || []).length;
      if (deletedCount > 0) {
        console.log(`Usunięto ${deletedCount} starych powiadomień (starszych niż 7 dni)`);
      }
    } catch (error) {
      console.error('Error cleaning up old notifications:', error);
    }
  };

  const loadNotifications = async () => {
    try {
      const { data, error } = await supabaseAdmin
        .from('push_notifications')
        .select('id, title, body, type, sent_at')
        .order('sent_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      const count = (data || []).length;
      console.log(`HISTORY COUNT: ${count}`);
      setNotifications(data || []);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    if (!isAdministrator) {
      toast.error('Tylko administrator może usuwać wpisy z historii powiadomień.');
      return;
    }
    if (!window.confirm('Czy na pewno chcesz usunąć to powiadomienie?')) return;

    try {
      const { error } = await supabaseAdmin
        .from('push_notifications')
        .delete()
        .eq('id', notificationId);

      if (error) throw error;
      toast.success('Powiadomienie usunięte');
      loadNotifications();
    } catch (error) {
      toast.error('Błąd podczas usuwania powiadomienia');
      console.error(error);
    }
  };

  const loadUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, device_id, expo_push_token, created_at')
        .not('expo_push_token', 'is', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      toast.error('Błąd podczas ładowania użytkowników');
      console.error(error);
    }
  };

  const loadRecipientCount = async () => {
    try {
      const { count, error } = await supabaseAdmin
        .from('users')
        .select('id', { count: 'exact', head: true })
        .not('expo_push_token', 'is', null);

      if (error) throw error;
      setRecipientCount(typeof count === 'number' ? count : 0);
    } catch (error) {
      console.error(error);
      toast.error('Błąd podczas pobierania liczby odbiorców');
      setRecipientCount(0);
    }
  };

  const sendPushNotification = async () => {
    if (!formData.title || !formData.body) {
      toast.error('Wypełnij wszystkie pola');
      return;
    }

    const sendToSpecific =
      isAdministrator && selectRecipientsManually && selectedUserIds.length > 0;
    if (isAdministrator && selectRecipientsManually && selectedUserIds.length === 0) {
      toast.error('Zaznacz co najmniej jednego odbiorcę');
      return;
    }

    if (!sendToSpecific && isAdministrator && users.length === 0) {
      toast.error('Brak użytkowników do wysłania powiadomienia');
      return;
    }
    if (!sendToSpecific && !isAdministrator && (recipientCount === 0 || recipientCount == null)) {
      toast.error('Brak użytkowników do wysłania powiadomienia');
      return;
    }

    setSending(true);
    try {
      const body = {
        title: formData.title,
        body: formData.body,
        type: formData.type,
      };
      if (sendToSpecific) body.user_ids = selectedUserIds;

      const { data, error } = await supabaseAdmin.functions.invoke('send-push-notification', {
        body,
      });

      if (error) {
        throw new Error(error.message || 'Błąd podczas wysyłania powiadomienia');
      }

      if (data?.error) {
        throw new Error(data.error + (data.details ? `: ${JSON.stringify(data.details)}` : ''));
      }

      if (data?.success) {
        const inserted = data.insertedCount ?? data.count ?? 0;
        const sent = data.sentCount ?? 0;
        toast.success(
          `Zapisano w aplikacji dla ${inserted} użytkowników` +
            (sent > 0
              ? ` (push OS: ${sent})`
              : ' (bez push OS — wyłączone u odbiorców lub brak tokena)')
        );
        setFormData({
          title: '',
          body: '',
          type: 'info',
          target: 'all',
          userId: '',
        });
        setSelectRecipientsManually(false);
        setSelectedUserIds([]);
        loadNotifications();
        if (isAdministrator) loadUsers();
        else loadRecipientCount();
      } else {
        throw new Error('Nieoczekiwana odpowiedź z serwera');
      }
    } catch (error) {
      const errorMessage = error.message || 'Błąd podczas wysyłania powiadomienia';
      toast.error(errorMessage);
      console.error('Error sending push notification:', error);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div className="loading">Ładowanie...</div>;
  }

  return (
    <div className="push-notifications">
      <h2>Wysyłanie powiadomień push</h2>

      <div className="card">
        <div className="form-group">
          <label className="form-label">Tytuł *</label>
          <input
            type="text"
            className="form-input"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="Tytuł powiadomienia"
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">Treść *</label>
          <textarea
            className="form-textarea"
            value={formData.body}
            onChange={(e) => setFormData({ ...formData, body: e.target.value })}
            placeholder="Treść powiadomienia"
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">Typ powiadomienia</label>
          <select
            className="form-input"
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
          >
            <option value="info">ℹ️ Informacja</option>
            <option value="warning">⚠️ Ostrzeżenie</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Odbiorcy</label>
          {isAdministrator && (
            <div className="form-group push-recipients-checkbox">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={selectRecipientsManually}
                  onChange={(e) => {
                    setSelectRecipientsManually(e.target.checked);
                    if (!e.target.checked) setSelectedUserIds([]);
                  }}
                />
                <span>Wybierz odbiorców ręcznie</span>
              </label>
            </div>
          )}
          {!selectRecipientsManually ? (
            <>
              <select className="form-input" value="all" disabled>
                <option value="all">
                  Wszyscy użytkownicy (
                  {isAdministrator ? users.length : recipientCount ?? '—'})
                </option>
              </select>
              <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {isAdministrator
                  ? 'Zaznacz powyższy checkbox, aby wybrać konkretnych odbiorców.'
                  : 'Wysyłka do wszystkich użytkowników z aktywnymi powiadomieniami push (bez podglądu listy urządzeń).'}
              </p>
            </>
          ) : (
            <div className="push-recipients-list">
              <p className="form-hint">Zaznacz użytkowników, do których chcesz wysłać powiadomienie:</p>
              {users.length === 0 ? (
                <p className="info-text">Brak użytkowników z tokenami push.</p>
              ) : (
                <div className="push-recipients-checkboxes">
                  {users.map((u) => (
                    <label key={u.id} className="checkbox-label push-recipient-item">
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(u.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedUserIds((prev) => [...prev, u.id]);
                          } else {
                            setSelectedUserIds((prev) => prev.filter((id) => id !== u.id));
                          }
                        }}
                      />
                      <span>{u.device_id || `ID: ${u.id}`}</span>
                    </label>
                  ))}
                </div>
              )}
              {selectedUserIds.length > 0 && (
                <p className="form-hint">Wybrano: {selectedUserIds.length} użytkowników</p>
              )}
            </div>
          )}
        </div>

        <button
          className="btn btn-primary btn-large"
          onClick={sendPushNotification}
          disabled={sending}
        >
          {sending ? 'Wysyłanie...' : 'Wyślij powiadomienie'}
        </button>
      </div>

      {isAdministrator && (
        <div className="card">
          <h3>Użytkownicy z aktywnymi powiadomieniami</h3>
          <p className="info-text">
            Liczba użytkowników z zarejestrowanymi tokenami push: <strong>{users.length}</strong>
          </p>
          {users.length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th>Device ID</th>
                  <th>Data rejestracji</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.device_id}</td>
                    <td>{new Date(user.created_at).toLocaleDateString('pl-PL')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="card">
        <h3>Historia wysłanych powiadomień</h3>
        {notifications.length > 0 ? (
          <table className="table">
            <thead>
              <tr>
                <th>Tytuł</th>
                <th>Treść</th>
                <th>Typ</th>
                <th>Data wysyłki</th>
                {isAdministrator && <th>Akcje</th>}
              </tr>
            </thead>
            <tbody>
              {notifications.map((notification) => {
                const decodedTitle = he.decode(notification.title || '');
                const decodedBody = he.decode(notification.body || '');
                const firstParagraph = decodedBody.split('\n\n')[0] || decodedBody;
                const preview = firstParagraph.length > 100 ? firstParagraph.substring(0, 97) + '...' : firstParagraph;
                return (
                <tr key={notification.id}>
                  <td>{decodedTitle}</td>
                  <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {preview}
                  </td>
                  <td>
                    {notification.type === 'warning' ? '⚠️ Ostrzeżenie' : 'ℹ️ Informacja'}
                  </td>
                  <td>{new Date(notification.sent_at).toLocaleString('pl-PL')}</td>
                  {isAdministrator && (
                    <td>
                      <button
                        type="button"
                        className="btn btn-danger btn-small"
                        onClick={() => handleDeleteNotification(notification.id)}
                      >
                        🗑️ Usuń
                      </button>
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="info-text">Brak wysłanych powiadomień</p>
        )}
      </div>
    </div>
  );
}

